import classNames from 'classnames';

import { LocaleLink } from '@components/LocaleLink';
import { ProductPackshot } from '@components/ProductPackshot';

import { CmsColor, CmsProductPackshot } from '@interfaces/common';
import { breakpoints } from '@utils/device';

import styles from './ProductCard.module.css';

interface ProductCardProps {
	slug: string;
	backgroundColor: CmsColor;
	title: string;
	description: string;
	subheading: string;
	packshot: CmsProductPackshot;
	locale?: string;
}

export const ProductCard: React.VFC<ProductCardProps> = ({
	slug,
	backgroundColor,
	locale,
	title,
	description,
	subheading,
	packshot,
}) => {
	const packshotAspectRatio = packshot.image.width / packshot.image.height;
	const widthMultiplier = packshotAspectRatio < 1 ? packshotAspectRatio : 1;

	const customProperties = {
		'--product-card-background-color': backgroundColor.hex,
		'--product-card-packshot-aspect-ratio': packshotAspectRatio,
	} as React.CSSProperties;

	// The numbers below are the `vw` for landscape images. We get the `vw`
	// for portrait images by multiplying with the `widthMultiplier`.
	const packshotSizes = [
		`${breakpoints.xxxl} ${20 * widthMultiplier}vw`,
		`${breakpoints.md} ${30 * widthMultiplier}vw`,
		`${62 * widthMultiplier}vw`,
	].join(',');

	return (
		<article className={styles.card} style={customProperties}>
			<div className={styles.content}>
				<header className={classNames(styles.header, 'fluid-font-t6-bold')}>
					<h2>
						<LocaleLink href={`/products/${slug}`} locale={locale} passHref>
							<a className="breakout">{title}</a>
						</LocaleLink>
					</h2>
					<p className={styles.subheading}>{subheading}</p>
				</header>
				<figure className={styles['packshot-wrapper']}>
					<ProductPackshot
						borderRadius={packshot.borderRadius}
						image={packshot.image}
						sizes={packshotSizes}
					/>
				</figure>
				<p className={classNames(styles.description, 'fluid-font-t6')}>
					{description}
				</p>
			</div>
		</article>
	);
};
